import { useShellCreateAction } from '@ordo/ui/app-shell'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Label } from '@ordo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { ServerDataGrid } from 'compositions/DataGrid'
import { Page } from 'compositions/Page'
import { useDuplicateContacts } from 'hooks/api'
import { AlertTriangle, Plus, Upload } from 'lucide-react'
import { parseAsStringLiteral, useQueryState } from 'nuqs'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import type { Contact } from 'types/contacts'
import type { Column } from 'types/data-grid'
import { formatPhone } from 'utils/phone'

import { DuplicateReviewDialog } from './duplicate-review-dialog'

const ACTIVE_FILTERS = ['active', 'inactive', 'all'] as const
const SUBSCRIPTION_FILTERS = ['all', 'subscribed', 'unsubscribed'] as const
type ActiveFilter = (typeof ACTIVE_FILTERS)[number]
type SubscriptionFilter = (typeof SUBSCRIPTION_FILTERS)[number]

const parseActiveFilter = parseAsStringLiteral(ACTIVE_FILTERS).withDefault('active')
const parseSubscriptionFilter = parseAsStringLiteral(SUBSCRIPTION_FILTERS).withDefault('subscribed')

function StatusBadges({ contact }: { contact: Contact }) {
  // Nothing to show for the default case (active + subscribed) — keep the
  // column visually quiet so the unusual states pop.
  if (contact.active && !contact.unsubscribedAt) {
    return null
  }
  return (
    <div className="flex flex-wrap gap-1">
      {!contact.active && (
        <Badge variant="outline" className="border-muted-foreground/40 text-xs font-normal text-muted-foreground">
          Inactive
        </Badge>
      )}
      {contact.unsubscribedAt && (
        <Badge
          variant="outline"
          className="border-yellow-600/60 bg-yellow-950/40 text-xs font-normal text-yellow-200"
          title={`Unsubscribed ${new Date(contact.unsubscribedAt).toLocaleDateString()}`}>
          Unsubscribed
        </Badge>
      )}
    </div>
  )
}

function fullName(row: Contact): string {
  return [row.firstName, row.lastName].filter(Boolean).join(' ').trim()
}

const COLUMNS: Column<Contact>[] = [
  {
    key: 'name',
    header: 'Name',
    sortable: true,
    filterable: true,
    render: (row) => fullName(row) || '(unnamed)',
  },
  { key: 'organizationName', header: 'Organization', sortable: true, filterable: true },
  { key: 'email', header: 'Email', sortable: true, filterable: true },
  {
    key: 'phoneNumber',
    header: 'Phone',
    width: '12%',
    sortable: true,
    filterable: true,
    render: (row) => formatPhone(row.phoneNumber),
  },
  { key: 'ordoUserId', header: 'Ordo ID', mobile: 'hidden', width: '10%', sortable: true, filterable: true },
  {
    key: '_status',
    header: 'Status',
    mobile: 'aside',
    width: '12%',
    render: (row) => <StatusBadges contact={row} />,
  },
]

export function ContactList() {
  const navigate = useNavigate()
  // On phones "New Contact" is the shell's floating + button; the header button is desktop only.
  useShellCreateAction({ label: 'New Contact', onCreate: () => navigate('/contacts/new') })

  const [activeFilter, setActiveFilter] = useQueryState('contacts.active', parseActiveFilter)
  const [subscriptionFilter, setSubscriptionFilter] = useQueryState('contacts.sub', parseSubscriptionFilter)

  const { data: duplicates, refetch: refetchDuplicates } = useDuplicateContacts()
  const [reviewOpen, setReviewOpen] = useState(false)

  // The grid's `params` change triggers a full reload. Derive only the server
  // filters the grid needs — the UI filter state stays local so toggling
  // between "all / active / inactive" just re-queries.
  const gridParams = useMemo(
    () => ({
      includeInactive: activeFilter !== 'active',
      ...(activeFilter === 'inactive' ? { active: false } : {}),
      ...(subscriptionFilter !== 'all' ? { subscription: subscriptionFilter } : {}),
    }),
    [activeFilter, subscriptionFilter],
  )

  const duplicateCount = useMemo(() => {
    if (!duplicates) {
      return 0
    }
    const uniqueIds = new Set<string>()
    for (const group of duplicates) {
      for (const c of group.contacts) {
        uniqueIds.add(c.id)
      }
    }
    return uniqueIds.size
  }, [duplicates])

  const handleReviewComplete = () => {
    refetchDuplicates()
  }

  return (
    <Page>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Contacts</h1>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="contacts-active-filter" className="text-sm text-muted-foreground">
              Active
            </Label>
            <Select value={activeFilter} onValueChange={(v) => setActiveFilter(v as ActiveFilter)}>
              <SelectTrigger id="contacts-active-filter" className="h-10 w-32 text-sm md:h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="contacts-sub-filter" className="text-sm text-muted-foreground">
              Subscription
            </Label>
            <Select value={subscriptionFilter} onValueChange={(v) => setSubscriptionFilter(v as SubscriptionFilter)}>
              <SelectTrigger id="contacts-sub-filter" className="h-10 w-40 text-sm md:h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="subscribed">Subscribed only</SelectItem>
                <SelectItem value="unsubscribed">Unsubscribed only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/contacts/import">
                <Upload className="h-4 w-4" />
                Import CSV
              </Link>
            </Button>
            <Button asChild className="hidden md:inline-flex">
              <Link to="/contacts/new">
                <Plus className="h-4 w-4" />
                New Contact
              </Link>
            </Button>
          </div>
        </div>
      </div>

      {duplicates && duplicates.length > 0 && (
        <div className="mb-4 flex items-center justify-between rounded-md border border-yellow-600/50 bg-yellow-950/30 px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-medium text-yellow-200">
            <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
            {duplicateCount} potential duplicate contacts found.
          </div>
          <Button variant="outline" size="sm" onClick={() => setReviewOpen(true)}>
            Review and merge
          </Button>
        </div>
      )}

      <ServerDataGrid<Contact>
        endpoint="/api/contacts"
        params={gridParams}
        columns={COLUMNS}
        keyField="id"
        storageKey="contacts"
        searchPlaceholder="Search name, email, phone, or organization…"
        onRowClick={(row) => navigate(`/contacts/${row.id}`)}
      />

      {duplicates && duplicates.length > 0 && (
        <DuplicateReviewDialog
          duplicates={duplicates}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          onComplete={handleReviewComplete}
        />
      )}
    </Page>
  )
}
